import React from "react";
import profTest from "../ProfTest.json";

const sectionOrder = ["activities", "abilities", "careers"];

const ProfTestWizard = () => {
  const [currentStep, setCurrentStep] = React.useState(0);
  // answers: { [sectionKey]: { [itemIndex]: boolean } }
  const [answers, setAnswers] = React.useState({});

  const sections = sectionOrder.map((key) => ({
    key,
    ...profTest.sections[key]
  }));

  const totalSteps = sections.length + 1; // +1 для шага с результатами

  const handleAnswer = (sectionKey, itemIndex, value) => {
    setAnswers((prev) => {
      const prevSection = prev[sectionKey] || {};
      return {
        ...prev,
        [sectionKey]: {
          ...prevSection,
          [itemIndex]: value
        }
      };
    });
  };

  const goNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goPrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const renderSectionStep = (section, index) => {
    const sectionAnswers = answers[section.key] || {};

    return React.createElement(
      "div",
      { className: "prof-test-step", key: section.key },
      React.createElement("h2", null, `${index + 1}. ${section.title}`),
      React.createElement(
        "p",
        { className: "prof-test-instruction" },
        section.instruction
      ),
      React.createElement(
        "div",
        { className: "prof-test-items" },
        section.items.map((item, itemIndex) => {
          const isChecked = !!sectionAnswers[itemIndex];

          return React.createElement(
            "label",
            {
              key: `${section.key}-${item.type}-${item.number}`,
              className: `prof-test-item ${
                isChecked ? "prof-test-item-checked" : ""
              }`
            },
            React.createElement(
              "div",
              { className: "prof-test-item-left" },
              React.createElement(
                "span",
                { className: "prof-test-item-number" },
                `${item.type}-${item.number}`
              ),
              React.createElement(
                "span",
                { className: "prof-test-item-text" },
                item.text
              )
            ),
            React.createElement(
              "div",
              { className: "prof-test-item-controls" },
              React.createElement("input", {
                type: "checkbox",
                className: "prof-test-checkbox",
                checked: isChecked,
                onChange: (e) =>
                  handleAnswer(section.key, itemIndex, e.target.checked)
              }),
              React.createElement("span", {
                className: "prof-test-checkbox-visual"
              })
            )
          );
        })
      )
    );
  };

  const computeScores = () => {
    const types = ["Р", "И", "А", "С", "П", "К"];
    const scores = {};
    types.forEach((t) => {
      scores[t] = {
        total: 0,
        bySection: {}
      };
    });

    sectionOrder.forEach((sectionKey) => {
      const section = profTest.sections[sectionKey];
      const sectionAnswers = answers[sectionKey] || {};

      section.items.forEach((item, index) => {
        const ans = sectionAnswers[index];
        if (ans) {
          if (!scores[item.type].bySection[sectionKey]) {
            scores[item.type].bySection[sectionKey] = 0;
          }
          scores[item.type].bySection[sectionKey] += 1;
          scores[item.type].total += 1;
        }
      });
    });

    const sortedTypes = Object.entries(scores)
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([type]) => type);

    const hollandCode = sortedTypes.slice(0, 3).join("");

    return { scores, hollandCode, sortedTypes };
  };

  const renderResults = () => {
    const { scores, hollandCode, sortedTypes } = computeScores();

    return React.createElement(
      "div",
      { className: "prof-test-results" },
      React.createElement(
        "h2",
        null,
        "Результаты теста"
      ),
      React.createElement(
        "p",
        null,
        "Ваш предполагаемый код Голланда: ",
        React.createElement("strong", null, hollandCode || "—")
      ),
      React.createElement(
        "p",
        null,
        profTest.scoring.instructions
      ),
      React.createElement(
        "div",
        { className: "prof-test-scores" },
        sortedTypes.map((type) => {
          const typeInfo = profTest.typeDescriptions[type];
          const typeScores = scores[type];

          return React.createElement(
            "div",
            {
              key: type,
              className: "prof-test-score-card"
            },
            React.createElement(
              "h3",
              null,
              `${type} — ${typeInfo ? typeInfo.title : ""} `
            ),
            React.createElement(
              "p",
              { className: "prof-test-score-total" },
              "Суммарный балл: ",
              React.createElement("strong", null, typeScores.total)
            ),
            React.createElement(
              "ul",
              { className: "prof-test-score-by-section" },
              sectionOrder.map((sectionKey) => {
                const section = profTest.sections[sectionKey];
                const value =
                  typeScores.bySection[sectionKey] || 0;
                return React.createElement(
                  "li",
                  { key: `${type}-${sectionKey}` },
                  React.createElement(
                    "strong",
                    null,
                    section.title,
                    ": "
                  ),
                  value
                );
              })
            ),
            typeInfo &&
              React.createElement(
                "details",
                { className: "prof-test-type-details" },
                React.createElement(
                  "summary",
                  null,
                  "Описание типа"
                ),
                React.createElement(
                  "p",
                  null,
                  typeInfo.description
                )
              )
          );
        })
      )
    );
  };

  const isResultsStep = currentStep === totalSteps - 1;

  return React.createElement(
    "div",
    { className: "prof-test-wizard" },
    React.createElement(
      "header",
      { className: "prof-test-header" },
      React.createElement(
        "h1",
        null,
        profTest.testTitle
      ),
      React.createElement(
        "p",
        { className: "prof-test-description" },
        profTest.testDescription
      )
    ),
    React.createElement(
      "div",
      { className: "prof-test-progress" },
      React.createElement(
        "span",
        null,
        "Шаг ",
        currentStep + 1,
        " из ",
        totalSteps
      )
    ),
    React.createElement(
      "main",
      { className: "prof-test-main" },
      isResultsStep
        ? renderResults()
        : renderSectionStep(sections[currentStep], currentStep)
    ),
    React.createElement(
      "footer",
      { className: "prof-test-footer" },
      React.createElement(
        "button",
        {
          type: "button",
          className: "prof-test-nav-btn",
          onClick: goPrev,
          disabled: currentStep === 0
        },
        "Назад"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          className: "prof-test-nav-btn",
          onClick: goNext,
          disabled: isResultsStep
        },
        isResultsStep ? "Готово" : "Далее"
      )
    )
  );
};

export default ProfTestWizard;

